import React, { useEffect } from 'react';
import { Route, Routes, useLocation, Outlet } from 'react-router-dom';
import { Layout } from './components/Layout';
import './custom.css';
import { Navigate } from 'react-router-dom';
import { useKeycloak } from './services/KeycloakProvider';
import { Index } from "./components/Index";
import { SignIn } from "./components/SignIn";
// import { FetchData } from './components/FetchData';
import { ConsentedRoute } from './ConsentedRoute';
import { CheckPage } from './components/pages/CheckPage';
import { ConsentPage } from './components/pages/ConsentPage';
import { ErrorPage } from './components/pages/ErrorPage';
import { TasksPage } from './components/pages/TasksPage';
import { TaskPage } from './components/pages/TaskPage';
import { OnboardingPage } from './components/pages/OnboardingPage';
import { RegisterPage } from './components/pages/RegisterPage';
import { NavMenu } from './components/NavMenu';
import { ApiServiceProvider } from './services/ApiServiceProvider';
import { HomePage } from './components/pages/HomePage';
import { useTranslation } from 'react-i18next';
// import AdminPage from './components/AdminPage.tsx';
import { ThankYouView } from './components/pages/ThankYouView';
import { LocalStorageProvider } from './services/LocalStorageProvider';
import { StorageProvider } from './services/StorageProvider';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { Roles } from './Roles';

// Lazy load DashboardPage to reduce main bundle size (Plotly is ~3-4MB)
const DashboardPage = React.lazy(() => import('./components/pages/DashboardPage'));

const PrivateRoute = ({ requiredRole }) => {
  const { keycloak, roles } = useKeycloak();
  const location = useLocation();
  const authenticated = keycloak.authenticated;

  if (!authenticated) {
    return <Navigate to={`/signin?path=${encodeURIComponent(location.pathname)}`} />;
  }

  if (requiredRole && !roles.includes(requiredRole)) {
    return <Navigate to={'/'} />; // replace with 403 page
  }

  return <Outlet />;
};

const DataCollection = () => {
  return <Outlet />;
}

const App = () => {
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Update document language attribute when language changes
  useEffect(() => {
    document.documentElement.lang = i18n.language || 'en';
  }, [i18n.language]);

  // Update document title on route change
  useEffect(() => {
    const pathTitleMap = {
      '/home': 'Home',
      '/onboarding': 'Onboarding',
      '/dashboard': 'Dashboard',
      '/data-collection/tasks': 'Tasks',
      '/consent': 'Consent Required',
      '/thank-you': 'Thank You',
      '/error': 'Error'
    };
    const base = 'B2Ai Voice';
    const title = pathTitleMap[location.pathname] ? `${pathTitleMap[location.pathname]} | ${base}` : base;
    document.title = title;
  }, [location]);

  return (
    <>
      {/* Skip link */}
      <a href="#main-content" className="skip-link" >{t('Skip to main content')}</a>
      
      <Layout>
        <ApiServiceProvider keycloak={keycloak}>
          <LocalStorageProvider>
            <StorageProvider>
              <NavMenu />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/check" element={<CheckPage />} />
                <Route element={<PrivateRoute />}>

                  <Route path="/home" element={<HomePage />} />

                  <Route element={<PrivateRoute requiredRole={Roles.OnboardingUser} />} >
                    <Route path="/onboarding" element={<OnboardingPage />} />
                  </Route>

                  <Route element={<PrivateRoute requiredRole={Roles.DashboardUser} />} >
                    <Route path="/dashboard" element={
                      <React.Suspense fallback={<LoadingSpinner />}>
                        <DashboardPage />
                      </React.Suspense>
                    } />
                  </Route>

                  <Route element={<ConsentedRoute />}>
                    <Route path="/data-collection" element={<DataCollection />}>
                      <Route path="tasks" element={<TasksPage />} />
                      <Route path="tasks/:taskName" element={<TaskPage />} />
                    </Route>
                  </Route>


                  <Route path="/consent" element={<ConsentPage />} />
                  <Route path="/thank-you" element={<ThankYouView />} />
                  <Route path="/error" element={<ErrorPage />} />
                </Route>
                {/* <Route element={<PrivateRoute requiredRole={'admin'} />}>
                  <Route path="/admin" element={<AdminPage />} />
                </Route> */}
              </Routes>
            </StorageProvider>
          </LocalStorageProvider>
        </ApiServiceProvider>
      </Layout>
    </>
  );
};

export default App;

